if (!customElements.get("progress-bar")) {
  customElements.define(
    "progress-bar",
    class ProgressBar extends HTMLElement {
      // État persisté par block (clé = id du wrapper #shopify-block-…) : un
      // static unique serait écrasé par la dernière instance connectée si
      // plusieurs barres de progression coexistent dans le tiroir.
      static _stateByBlock = new Map();
      // Animation uniquement : page chargée panier vide, aucune instance n'a
      // capturé de total. Le premier ajout doit alors animer la barre depuis
      // 0 plutôt que l'afficher directement à sa largeur finale.
      static _emptyBaseline = false;
      // Produits offerts des étapes : un seul enchaînement de requêtes en vol
      // pour tout le tiroir. Une barre reconnectée pendant ce temps se
      // contente de laisser une demande, rejouée à la fin.
      static _syncInFlight = false;
      static _syncPending = false;
      // Variantes ajoutées avec succès mais pas encore vues dans un rendu du
      // panier. Tant qu'elles y figurent, pas de nouvel ajout : un rendu en
      // retard ne doit ni doubler la ligne, ni relancer l'ajout en boucle si
      // le panier ne le reflète pas. Levé dès qu'un rendu montre la ligne, ou
      // que le produit n'est plus dû.
      static _awaitingLine = new Set();
      // Variantes dont l'ajout a été refusé (rupture, limite d'achat) : pas de
      // nouvel essai tant que le palier reste atteint, sinon chaque re-rendu
      // du tiroir relancerait la même requête vouée à l'échec.
      static _refusedVariants = new Set();
      // Ajouts réussis par variante depuis le chargement de la page. Au-delà
      // de MAX_ADDS_PER_PAGE, plus d'ajout : seule une configuration qui fait
      // osciller le palier avec la présence du cadeau (réduction qui compte
      // la ligne offerte, par exemple) y mène, et elle ajouterait puis
      // retirerait le produit en boucle. La page suivante repart de zéro.
      static _addCounts = new Map();
      static MAX_ADDS_PER_PAGE = 3;
      // Mémoire des produits offerts que la barre a ajoutés au panier. La
      // ligne offerte n'a aucune propriété (rien de technique ne doit arriver
      // sur la commande) : Shopify fusionne donc l'unité offerte avec les
      // unités achetées de la même variante, et seule cette mémoire dit
      // laquelle est offerte. Forme : { token, added: { variante: unités
      // achetées au moment de l'ajout }, declined: [variantes] }. Liée au
      // jeton du panier : un panier neuf (commande passée, panier expiré)
      // repart de zéro. Mémoire de page si le stockage est indisponible.
      static GIFT_STATE_KEY = "progress-bar-gifts";
      static _memoryGiftState = null;

      _stateKey() {
        const wrapper = this.closest('[id^="shopify-block-"]');
        return wrapper ? wrapper.id : "progress-bar";
      }

      connectedCallback() {
        this.steps = this.querySelectorAll(".cart-drawer-progress-bar-advanced");
        this.wrapper = this.querySelector(".progress-bar__wrapper");
        this.dragEl = this.querySelector(".progress-bar__bar--drag");
        this.target = +this.dataset.target;
        this.cartTotal = +this.dataset.cartTotal || 0;
        this.allUnlockedMessage = this.dataset.allUnlockedMessage || "";
        this.titleEl = this.querySelector(".progress-bar__title");

        this.HORIZONTAL_BUFFER = 8;
        this._lastCartTotal = this.cartTotal;
        this._isFirstRun = true;

        if (!this.steps.length) {
          // Rien a animer, mais tout a synchroniser : sans etape plus rien
          // n'est du, et les produits deja ajoutes par la barre doivent partir.
          this.syncRewards();
          return;
        }

        const C = this.constructor;
        const prev =
          C._stateByBlock.get(this._stateKey()) ||
          (C._emptyBaseline ? { cartTotal: 0, dragPercent: 0 } : null);
        // La base « panier vide » n'a servi qu'à ce premier connect.
        C._emptyBaseline = false;
        const animateOnConnect =
          !!prev && prev.cartTotal !== this.cartTotal && !!this.dragEl;

        if (animateOnConnect) {
          // The element is server-rendered with the NEW width and the
          // `.is-no-transition` class. We rewind it to the PREVIOUS width
          // without animation, flush layout, then drop the class so the
          // subsequent assignment to the new width plays the CSS transition.
          this._isInitialAnimating = true;

          this.dragEl.style.width = `${prev.dragPercent}%`;
          this.dragEl.offsetWidth;
          this.dragEl.classList.remove("is-no-transition");
          this._isFirstRun = false;

          this.updateTitle();
          this.applyMaxHeight();
          this.distributeAndFill({ animate: true });

          const endAnimation = () => {
            // transitionend et le repli minute peuvent tous deux arriver :
            // une seule fin d'animation.
            if (!this._isInitialAnimating) return;
            this._isInitialAnimating = false;
            if (this._animationFallbackTimer) {
              clearTimeout(this._animationFallbackTimer);
              this._animationFallbackTimer = null;
            }
            // La capture faite au connect a lieu avant la fin de la
            // transition : on re-capture ici la largeur réellement atteinte
            // pour que le prochain rembobinage parte de la bonne position.
            this._capturePersistedState();
            // La synchronisation attend la fin de l'animation : sa réponse
            // re-rend le tiroir, ce qui couperait net la transition.
            this.syncRewards();
          };
          this.dragEl.addEventListener("transitionend", endAnimation, { once: true });
          this._animationFallbackTimer = setTimeout(endAnimation, 900);
        } else {
          this.distributeAndFill({ animate: false });
          this.updateTitle();
          this.applyMaxHeight();

          requestAnimationFrame(() => {
            if (this.dragEl) this.dragEl.classList.remove("is-no-transition");
            this._isFirstRun = false;
            this.syncRewards();
          });
        }

        this._capturePersistedState();

        this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
        if (this.wrapper) this.resizeObserver.observe(this.wrapper);
        this.steps.forEach((step) => this.resizeObserver.observe(step));

        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.scheduleLayout());
        }

        this.querySelectorAll(".cart-drawer-progress-bar-advanced img").forEach((img) => {
          if (!img.complete) {
            img.addEventListener("load", () => this.scheduleLayout(), { once: true });
          }
        });

        // Filet pour les mutations du panier qui ne re-rendent PAS le tiroir
        // (page /cart notamment) : le nouveau total est reflété tout de
        // suite, puis le tiroir est re-rendu depuis le serveur pour que l'état
        // des étapes et des lignes offertes, calculé en Liquid, soit frais.
        if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          this._cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) =>
            this._onExternalCartUpdate(event)
          );
        }
      }

      _onExternalCartUpdate(event) {
        // Nos propres ajouts / retraits : le tiroir a déjà été re-rendu.
        if (!event || event.source === "progress-bar-reward") return;
        const cart = event.cartData;
        // Seule la forme "panier complet" (réponses /cart/change|update) est
        // exploitable ; les réponses /cart/add (product-form) n'ont pas
        // items_subtotal_price, et ce chemin re-rend de toute façon le
        // tiroir (la synchronisation se fait alors au reconnect).
        // Même montant que cart.items_subtotal_price côté Liquid.
        const total = cart && cart.items_subtotal_price;
        if (typeof total !== "number") return;
        // Chemin quantité du tiroir : .drawer__inner est remplacé AVANT la
        // publication, la nouvelle instance porte déjà ce total.
        if (total === this.cartTotal) return;

        this.cartTotal = total;
        this.dataset.cartTotal = total;
        this.distributeAndFill({ animate: false });
        this.updateTitle();
        // Re-capture la largeur réellement appliquée pour que la prochaine
        // reconnexion animée reparte de la bonne position.
        this._capturePersistedState();

        // L'état des étapes (palier atteint ou non) et des lignes offertes vit
        // en Liquid : seul un rendu frais du tiroir le connaît. La barre
        // reconnectée ajoutera ou retirera ce qu'il faut. Jamais en mode design.
        if (window.Shopify && window.Shopify.designMode) return;
        this._refreshDrawerFromServer();
      }

      disconnectedCallback() {
        if (this._cartUpdateUnsubscriber) {
          this._cartUpdateUnsubscriber();
          this._cartUpdateUnsubscriber = null;
        }
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer && cartDrawer.classList.contains("is-empty")) {
          // Panier vidé : oublier les totaux capturés et repartir d'une base
          // 0 pour que le premier ajout suivant anime la barre depuis 0.
          // Le jeton du panier survit au vidage : la mémoire des produits
          // offerts doit partir ici, sinon le prochain palier atteint
          // prendrait le cadeau disparu pour un refus du client.
          this.constructor._stateByBlock.clear();
          this.constructor._emptyBaseline = true;
          this.constructor._clearGiftState();
        } else {
          this._capturePersistedState();
        }
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this._rafId) cancelAnimationFrame(this._rafId);
        if (this._animationFallbackTimer) clearTimeout(this._animationFallbackTimer);
      }

      _capturePersistedState() {
        if (!this.dragEl) return;
        let pct = null;
        const barEl = this.dragEl.parentElement;
        const barWidth = barEl ? barEl.getBoundingClientRect().width : 0;
        if (barWidth > 0) {
          pct = (this.dragEl.getBoundingClientRect().width / barWidth) * 100;
        } else {
          // Élément détaché (swap innerHTML / replaceWith) : les rects valent
          // 0. On retombe sur la largeur cible posée en style inline, qui est
          // toujours exprimée en pourcentage.
          const styleWidth = parseFloat(this.dragEl.style.width);
          if (Number.isFinite(styleWidth)) pct = styleWidth;
        }
        if (pct == null || !Number.isFinite(pct)) return;
        this.constructor._stateByBlock.set(this._stateKey(), {
          cartTotal: this.cartTotal,
          dragPercent: Math.max(0, Math.min(100, pct)),
        });
      }

      scheduleLayout() {
        if (this._rafId) cancelAnimationFrame(this._rafId);
        this._rafId = requestAnimationFrame(() => {
          // While the mount-time transition is running, animate:true keeps
          // setBarWidth from temporarily adding `.is-no-transition`, which
          // would snap the in-flight CSS transition to its end value.
          const animate = !!this._isInitialAnimating;
          this.distributeAndFill({ animate });
          this.applyMaxHeight();
        });
      }

      setBarWidth(pct, animate) {
        if (!this.dragEl) return;
        const clamped = Math.max(0, Math.min(100, pct));

        if (!animate) {
          this.dragEl.classList.add("is-no-transition");
          this.dragEl.style.width = `${clamped}%`;
          this.dragEl.offsetWidth;
          if (!this._isFirstRun) {
            this.dragEl.classList.remove("is-no-transition");
          }
        } else {
          this.dragEl.style.width = `${clamped}%`;
        }
      }

      distributeAndFill({ animate = false } = {}) {
        if (!this.steps.length || !this.wrapper) return;

        const wrapperWidth = this.wrapper.getBoundingClientRect().width;
        if (wrapperWidth <= 0) return;

        const targetCents = this.target * 100;
        if (targetCents <= 0) return;

        Array.from(this.steps).forEach((step) => {
          step.style.maxWidth = "";
        });

        const stepData = Array.from(this.steps)
          .map((el) => {
            const dataEl = el.querySelector(".progress-bar__step-data");
            const amount = dataEl ? +dataEl.dataset.amount : 0;
            return { el, amount, width: el.offsetWidth };
          })
          .filter((s) => s.amount > 0)
          .sort((a, b) => a.amount - b.amount);

        if (!stepData.length) return;

        const positions = stepData.map((s) =>
          Math.min((s.amount / targetCents) * wrapperWidth, wrapperWidth)
        );

        for (let i = 1; i < positions.length; i++) {
          const minPos =
            positions[i - 1] +
            (stepData[i - 1].width + stepData[i].width) / 2 +
            this.HORIZONTAL_BUFFER;
          if (positions[i] < minPos) positions[i] = minPos;
        }

        const lastIdx = positions.length - 1;
        const rightEdge = wrapperWidth - stepData[lastIdx].width / 2;
        if (positions[lastIdx] > rightEdge) {
          positions[lastIdx] = rightEdge;
          for (let i = lastIdx - 1; i >= 0; i--) {
            const maxAllowed =
              positions[i + 1] -
              (stepData[i].width + stepData[i + 1].width) / 2 -
              this.HORIZONTAL_BUFFER;
            if (positions[i] > maxAllowed) positions[i] = maxAllowed;
          }
        }

        if (positions[0] < stepData[0].width / 2) {
          positions[0] = stepData[0].width / 2;
          for (let i = 1; i < positions.length; i++) {
            const minPos =
              positions[i - 1] +
              (stepData[i - 1].width + stepData[i].width) / 2 +
              this.HORIZONTAL_BUFFER;
            if (positions[i] < minPos) positions[i] = minPos;
          }
        }

        stepData.forEach((s, i) => {
          const pct = (positions[i] / wrapperWidth) * 100;
          s.el.style.left = `${pct}%`;
          s.visualPct = pct;
        });

        this.updateBarFill(stepData, targetCents, animate);
      }

      updateBarFill(stepData, targetCents, animate) {
        if (!this.dragEl) return;

        const lastAmount = stepData[stepData.length - 1].amount;

        if (this.cartTotal >= lastAmount) {
          this.setBarWidth(100, animate);
          return;
        }

        const points = [{ amount: 0, pct: 0 }];
        stepData.forEach((s) => points.push({ amount: s.amount, pct: s.visualPct }));
        if (lastAmount < targetCents) {
          points.push({ amount: targetCents, pct: 100 });
        }

        let pct = 0;
        for (let i = 0; i < points.length - 1; i++) {
          if (this.cartTotal >= points[i].amount && this.cartTotal <= points[i + 1].amount) {
            const span = points[i + 1].amount - points[i].amount;
            const t = span > 0 ? (this.cartTotal - points[i].amount) / span : 0;
            pct = points[i].pct + t * (points[i + 1].pct - points[i].pct);
            break;
          }
        }

        this.setBarWidth(pct, animate);
      }

      updateTitle() {
        if (!this.titleEl) return;

        const stepsData = Array.from(this.steps)
          .map((step) => {
            const data = step.querySelector(".progress-bar__step-data");
            if (!data) return null;
            return {
              amount: +(data.dataset.amount || 0),
              remaining: data.dataset.remaining || ""
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.amount - b.amount);

        let nextStep = null;
        stepsData.forEach((step) => {
          if (this.cartTotal < step.amount && !nextStep) {
            nextStep = step;
          }
        });

        if (nextStep) {
          this.titleEl.innerHTML = nextStep.remaining;
        } else if (stepsData.length > 0) {
          this.titleEl.innerHTML = this.allUnlockedMessage;
        }
      }

      applyMaxHeight() {
        if (!this.wrapper || !this.steps.length) return;

        const stepOffset = this.steps[0].offsetTop;
        const maxStepHeight = Math.max(
          ...Array.from(this.steps).map((el) => el.offsetHeight)
        );

        this.wrapper.style.height = `${stepOffset + maxStepHeight}px`;
      }

      static _readGiftState() {
        try {
          return JSON.parse(window.localStorage.getItem(this.GIFT_STATE_KEY));
        } catch (e) {
          return this._memoryGiftState;
        }
      }

      static _loadGiftState(token) {
        const saved = this._readGiftState();
        const same = !!saved && saved.token === token;
        const added = {};
        if (same && saved.added && typeof saved.added === "object") {
          Object.keys(saved.added).forEach((id) => {
            added[id] = Math.max(0, parseInt(saved.added[id], 10) || 0);
          });
        }
        return {
          token,
          added,
          declined: new Set(same && Array.isArray(saved.declined) ? saved.declined.map(String) : []),
        };
      }

      static _saveGiftState(state) {
        const data = { token: state.token, added: state.added, declined: Array.from(state.declined) };
        const isEmpty = !Object.keys(data.added).length && !data.declined.length;
        this._memoryGiftState = isEmpty ? null : data;
        try {
          if (isEmpty) window.localStorage.removeItem(this.GIFT_STATE_KEY);
          else window.localStorage.setItem(this.GIFT_STATE_KEY, JSON.stringify(data));
        } catch (e) {}
      }

      static _clearGiftState() {
        this._memoryGiftState = null;
        try {
          window.localStorage.removeItem(this.GIFT_STATE_KEY);
        } catch (e) {}
      }

      // Ce qu'il faut changer au panier pour qu'il porte exactement les
      // produits dus. Un produit est dû quand son étape a un produit, que le
      // montant du panier atteint le sien (`data-reward-reached`, calculé en
      // Liquid sur le panier rendu) et que l'option de la barre est sur Oui
      // (`data-rewards-enabled`).
      //
      // L'unité offerte vit dans la ligne ordinaire de sa variante, avec les
      // unités que le client a achetées. Unités offertes = quantité de la
      // ligne moins les unités achetées notées au moment de l'ajout, au plus
      // une. La barre ne retire jamais que cette unité : un achat n'est
      // jamais touché. Le client qui fait descendre la ligne sous ce seuil a
      // retiré le cadeau : il n'est plus rajouté tant que le palier tient.
      _rewardPlan() {
        const C = this.constructor;
        const state = C._loadGiftState(this.dataset.cartToken || "");
        let stateChanged = false;

        const wanted = new Set();
        document.querySelectorAll("progress-bar").forEach((bar) => {
          if (bar.dataset.rewardsEnabled === "false") return;
          bar.querySelectorAll("[data-reward-variant-id]").forEach((el) => {
            const id = el.dataset.rewardVariantId;
            if (id && el.dataset.rewardReached === "true") wanted.add(id);
          });
        });

        // Ligne ordinaire de chaque variante dans le panier rendu (bloc
        // parent) : celle où Shopify range l'unité ajoutée par la barre.
        const lines = new Map();
        this.querySelectorAll("[data-line-key]").forEach((el) => {
          const id = el.dataset.lineVariantId;
          if (!id || lines.has(id)) return;
          lines.set(id, {
            key: el.dataset.lineKey,
            quantity: parseInt(el.dataset.lineQuantity, 10) || 0,
          });
        });
        const lineOf = (id) => lines.get(id) || { key: null, quantity: 0 };

        // Plus dû (palier repassé, option sur Non, produit changé ou retiré
        // de l'étape) : retirer l'unité offerte si elle est encore là.
        const updates = {};
        const released = [];
        // Transition, à retirer après le 15/11/2026 avec son pendant Liquid :
        // ligne offerte de l'ancienne version (propriété de ligne), retirée.
        // L'ajout ci-dessous la remplace si le palier tient.
        this.querySelectorAll("[data-legacy-line-key]").forEach((el) => {
          if (el.dataset.legacyLineKey) updates[el.dataset.legacyLineKey] = 0;
        });
        Object.keys(state.added).forEach((id) => {
          if (wanted.has(id)) return;
          const line = lineOf(id);
          if (line.key && line.quantity > state.added[id]) {
            updates[line.key] = line.quantity - 1;
            released.push(id);
          } else {
            delete state.added[id];
            stateChanged = true;
          }
        });

        // Plus dû : un prochain franchissement pourra l'ajouter de nouveau.
        Array.from(state.declined).forEach((id) => {
          if (wanted.has(id)) return;
          state.declined.delete(id);
          stateChanged = true;
        });
        [C._awaitingLine, C._refusedVariants].forEach((set) => {
          Array.from(set).forEach((id) => {
            if (!wanted.has(id)) set.delete(id);
          });
        });

        const items = [];
        const purchasedBefore = {};
        wanted.forEach((id) => {
          const quantity = lineOf(id).quantity;
          const giftPresent = id in state.added && quantity > state.added[id];
          if (C._awaitingLine.has(id)) {
            // Rendu antérieur à l'ajout accepté : ne rien conclure.
            if (!giftPresent) return;
            C._awaitingLine.delete(id);
          }
          if (id in state.added) {
            if (giftPresent) return;
            delete state.added[id];
            state.declined.add(id);
            stateChanged = true;
            return;
          }
          if (state.declined.has(id) || C._refusedVariants.has(id)) return;
          if ((C._addCounts.get(id) || 0) >= C.MAX_ADDS_PER_PAGE) {
            console.warn(
              `[progress-bar] produit offert ${id} ajouté ${C.MAX_ADDS_PER_PAGE} fois sur cette page : ajout suspendu (oscillation du palier ?)`
            );
            return;
          }
          purchasedBefore[id] = quantity;
          items.push({ id: Number(id), quantity: 1 });
        });

        if (stateChanged) C._saveGiftState(state);
        return { updates, released, items, purchasedBefore, state };
      }

      syncRewards(isRetry) {
        // Aligne le panier sur les produits dus (voir _rewardPlan) : retire
        // d'abord (POST /cart/update.js), ajoute ensuite (POST /cart/add.js,
        // un produit par requête pour qu'un refus n'en bloque pas un autre),
        // puis re-rend le tiroir avec les sections de la dernière réponse.
        //
        // Jamais en mode design : le panier du marchand serait réellement
        // modifié depuis l'éditeur de thème.
        if (window.Shopify && window.Shopify.designMode) return;
        if (
          typeof window.routes === "undefined" ||
          !window.routes.cart_add_url ||
          !window.routes.cart_update_url
        ) {
          return;
        }
        if (typeof fetchConfig !== "function") return;
        // Seule la première barre du document agit, pour le compte de toutes.
        // Elle doit être une barre qui synchronise vraiment : une barre encore
        // en cours de connexion, élue par sa seule présence dans le DOM,
        // laisserait toutes les autres se retirer et personne n'agirait.
        this.dataset.rewardSync = "";
        if (document.querySelector("progress-bar[data-reward-sync]") !== this) return;

        const C = this.constructor;
        if (C._syncInFlight) {
          C._syncPending = true;
          return;
        }

        const { updates, released, items, purchasedBefore, state } = this._rewardPlan();
        const hasUpdates = Object.keys(updates).length > 0;
        if (!hasUpdates && !items.length) return;

        C._syncInFlight = true;
        // Instantané du jeton de rendu : si un rendu plus récent du tiroir
        // survient pendant les requêtes, leurs sections sont périmées.
        const seqAtStart = window.__cartDrawerRenderSeq || 0;
        const sectionsParams = {
          sections: "cart-drawer-advanced,cart-icon-bubble",
          sections_url: window.location.pathname,
        };
        const post = (url, payload) =>
          fetch(url, { ...fetchConfig(), body: JSON.stringify({ ...payload, ...sectionsParams }) })
            .then((res) => res.json().then((data) => ({ ok: res.ok, data })));
        // Réponses acceptées, dans l'ordre : la dernière porte l'état final du
        // tiroir ; toutes sont publiées (page /cart, pastille, analytics).
        const accepted = [];

        let chain = Promise.resolve();
        if (hasUpdates) {
          chain = chain
            .then(() => post(window.routes.cart_update_url, { updates }))
            .then(({ ok, data }) => {
              if (!ok) {
                throw new Error(
                  (data && (data.description || data.message)) || `HTTP ${JSON.stringify(data)}`
                );
              }
              released.forEach((id) => delete state.added[id]);
              C._saveGiftState(state);
              accepted.push(data);
            });
        }
        items.forEach((item) => {
          chain = chain
            .then(() => post(window.routes.cart_add_url, { items: [item] }))
            .then(({ ok, data }) => {
              const id = String(item.id);
              if (!ok) {
                // 422 typique : produit épuisé ou limite d'achat atteinte
                // depuis le rendu du tiroir.
                C._refusedVariants.add(id);
                console.error(
                  "[progress-bar] ajout du produit offert refusé :",
                  (data && (data.description || data.message)) || data
                );
                return;
              }
              state.added[id] = purchasedBefore[id] || 0;
              C._saveGiftState(state);
              C._awaitingLine.add(id);
              C._addCounts.set(id, (C._addCounts.get(id) || 0) + 1);
              accepted.push(data);
            });
        });

        const applyAccepted = () => {
          if (!accepted.length) return;
          const isFresh = seqAtStart === (window.__cartDrawerRenderSeq || 0);
          const rendered = isFresh && this._renderDrawerSections(accepted[accepted.length - 1]);
          if (!rendered) {
            // Soit le tiroir a été re-rendu pendant les requêtes (nos
            // sections sont un instantané antérieur), soit la réponse ne
            // contenait pas de sections exploitables : on re-fetch l'état
            // frais pour ne jamais laisser le tiroir obsolète.
            this._refreshDrawerFromServer();
          }
          if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
            // Même forme que les autres émetteurs (cartData = réponse de
            // /cart/update.js ou /cart/add.js) : publish() n'isole pas ses
            // abonnés, un événement sans cartData en ferait échouer certains.
            accepted.forEach((data) => {
              const { sections, ...cartData } = data;
              publish(PUB_SUB_EVENTS.cartUpdate, { source: "progress-bar-reward", cartData });
            });
          }
        };

        let failure = null;
        chain
          .catch((e) => {
            failure = e;
          })
          .then(() => {
            // Ce qui a été accepté est appliqué, même si une requête suivante
            // a échoué.
            applyAccepted();
            if (!failure) return;
            // 429 fréquent juste après une mutation du panier, ou coupure
            // réseau : un unique nouvel essai différé. Si le tiroir vient
            // d'être re-rendu, c'est la nouvelle barre qui reprendra.
            console.error("[progress-bar] produits offerts :", failure);
            if (!isRetry) {
              setTimeout(() => {
                if (this.isConnected) this.syncRewards(true);
              }, 1000);
            }
          })
          .catch((e) => console.error("[progress-bar] rendu après produits offerts :", e))
          .finally(() => {
            C._syncInFlight = false;
            if (C._syncPending) {
              // Une barre reconnectée pendant les requêtes a laissé une
              // demande : c'est elle, désormais première du document, qui
              // reprend.
              C._syncPending = false;
              const bar = document.querySelector("progress-bar[data-reward-sync]");
              if (bar && bar.isConnected && typeof bar.syncRewards === "function") {
                bar.syncRewards();
              }
            }
          });
      }

      _renderDrawerSections(data) {
        // Retourne true seulement si le tiroir a réellement été re-rendu ;
        // l'appelant retombe sinon sur un re-fetch de l'état serveur.
        const sections = data && data.sections;
        if (!sections || !sections["cart-drawer-advanced"]) return false;

        const doc = new DOMParser().parseFromString(
          sections["cart-drawer-advanced"],
          "text/html"
        );
        const drawerEl = document.querySelector("cart-drawer");
        const inner = drawerEl && drawerEl.querySelector(".drawer__inner");
        const newInner = doc.querySelector(".drawer__inner");
        if (!inner || !newInner) return false;

        // Invalide les rafraîchissements du tiroir (GET de onCartUpdate)
        // partis avant cet état : leur réponse serait périmée. Uniquement
        // maintenant que le swap est certain, pour ne pas tuer un GET
        // correctif si on avait dû abandonner.
        window.__cartDrawerRenderSeq = (window.__cartDrawerRenderSeq || 0) + 1;

        // La classe is-empty de l'hôte pilote le layout, le conteneur de
        // trapFocus à l'ouverture ET la remise à zéro de l'état persisté
        // dans le disconnectedCallback des <progress-bar> détruites par le
        // swap : elle doit être posée AVANT l'affectation innerHTML (même
        // ordre que CartItems.updateQuantity), d'après l'état serveur rendu.
        const newDrawerHost = doc.querySelector("cart-drawer");
        if (newDrawerHost) {
          drawerEl.classList.toggle(
            "is-empty",
            newDrawerHost.classList.contains("is-empty")
          );
        }

        const focusWasInside = inner.contains(document.activeElement);
        inner.innerHTML = newInner.innerHTML;

        // Le swap détruit l'élément focalisé s'il était dans le tiroir : sans
        // re-trap, le bouclage clavier du dialogue pointe des nœuds détachés.
        // On ne vole pas le focus s'il était ailleurs (saisie hors tiroir).
        if (
          focusWasInside &&
          drawerEl.classList.contains("active") &&
          typeof trapFocus === "function"
        ) {
          const container = document.getElementById("CartDrawer");
          if (container) trapFocus(container, inner);
        }

        const bubble = document.getElementById("cart-icon-bubble");
        if (bubble && sections["cart-icon-bubble"]) {
          const bubbleDoc = new DOMParser().parseFromString(
            sections["cart-icon-bubble"],
            "text/html"
          );
          const src = bubbleDoc.querySelector(".shopify-section");
          if (src) bubble.innerHTML = src.innerHTML;
        }
        return true;
      }

      _refreshDrawerFromServer(isRetry) {
        // Repli : re-rend le tiroir ET la pastille du panier depuis l'état
        // serveur courant. Utilisé après une mutation du panier qui n'a pas
        // re-rendu le tiroir (page /cart), ou quand le rendu direct des
        // sections de la réponse des produits offerts est impossible ou périmé.
        // Un unique retry couvre le throttling (429) fréquent juste après
        // un POST sur le panier.
        //
        // Le jeton est INCRÉMENTÉ à l'émission, comme dans cart.js : le
        // dernier GET parti gagne, les précédents s'auto-invalident. Un
        // simple instantané laissait deux GET concurrents appliquer le plus
        // ancien en dernier, et le tiroir sur un panier périmé.
        if (typeof window.routes === "undefined" || !window.routes.cart_url) return;
        const seq = (window.__cartDrawerRenderSeq = (window.__cartDrawerRenderSeq || 0) + 1);
        fetch(`${window.routes.cart_url}?sections=cart-drawer-advanced,cart-icon-bubble`)
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
          })
          .then((sections) => {
            if (seq !== (window.__cartDrawerRenderSeq || 0)) return;
            this._renderDrawerSections({ sections });
          })
          .catch((e) => {
            console.error("[progress-bar] rafraîchissement du tiroir impossible :", e);
            if (!isRetry) {
              setTimeout(() => this._refreshDrawerFromServer(true), 1000);
            }
          });
      }
    }
  );
}

// Panier vide au chargement de la page : aucune <progress-bar> n'est rendue
// (le tiroir affiche l'état "sans produit"), donc aucun connect ne capture de
// total de départ. On amorce la base à 0 pour que le premier ajout anime la
// barre depuis 0. La mémoire des produits offerts d'un panier vidé hors de
// la vue d'une barre part aussi.
(() => {
  const drawer = document.querySelector("cart-drawer");
  const ProgressBarClass = customElements.get("progress-bar");
  if (drawer && drawer.classList.contains("is-empty") && ProgressBarClass) {
    ProgressBarClass._emptyBaseline = true;
    ProgressBarClass._clearGiftState();
  }
})();
